/**
 * Shopify's cookie banner in Serbian, without headings.
 *
 * The banner is Shopify's own (privacy-banner/storefront-banner.js). It has no
 * Serbian copy, so EU visitors got it in English, and its titles are <h2>/<h3>,
 * which put "Cookie consent" into every page's heading outline. The admin can
 * change the wording but not the tags, so both are fixed here, after Shopify
 * renders the dialog. Everything is keyed on Shopify's element ids: if they
 * change, the banner simply stays as Shopify ships it.
 */
(() => {
  const POLICY_URL = '/policies/privacy-policy';

  const TEXT = {
    'shopify-pc__banner__body-title': 'Kolačići',
    'shopify-pc__banner__btn-manage-prefs': 'Podešavanja',
    'shopify-pc__banner__btn-accept': 'Prihvatam',
    'shopify-pc__banner__btn-decline': 'Odbijam',
    'shopify-pc__prefs__header-title': 'Podešavanja kolačića i privatnosti',
    'shopify-pc__prefs__header-accept': 'Prihvati sve',
    'shopify-pc__prefs__header-decline': 'Odbij sve',
    'shopify-pc__prefs__header-save': 'Sačuvaj izbor',
  };

  // Category rows: [label, description]
  const OPTIONS = {
    'shopify-pc__prefs__essential': [
      'Neophodni',
      'Bez njih sajt ne radi kako treba — na primer, ne biste mogli da dodate proizvod u korpu i poručite.',
    ],
    'shopify-pc__prefs__preferences': [
      'Personalizacija',
      'Pamte šta ste radili na sajtu, da bi vam sledeća poseta bila prilagođena.',
    ],
    'shopify-pc__prefs__marketing': [
      'Marketing',
      'Koristimo ih mi i naši partneri, uključujući Shopify, da bismo prilagodili poruke i prikazivali vam oglase na drugim sajtovima.',
    ],
    'shopify-pc__prefs__analytics': [
      'Analitika',
      'Pomažu nam da razumemo kako koristite sajt, da bismo znali šta da poboljšamo.',
    ],
  };

  // Shopify styled the titles through their tags (`… h2`, `… h3`); the same
  // look, for the <p> that replaces them.
  const style = document.createElement('style');
  style.textContent = `
    .pc-title { color: hsl(0 0% 12%); font-family: inherit; padding: 0; }
    #shopify-pc__banner .pc-title--banner { font-size: 120%; font-weight: 700; margin: 0 0 .5em; }
    #shopify-pc__prefs__dialog .pc-title--prefs { font-size: 130%; font-weight: 600; line-height: 1.2; margin: 0; padding: 0 20px 0 0; width: 100%; text-align: start; }
    #shopify-pc__prefs__dialog .pc-title--intro { font-size: 110%; font-weight: 600; line-height: 1.2; margin: 0 0 15px; }
    @media only screen and (max-width: 1200px) {
      #shopify-pc__prefs__dialog .pc-title--prefs { margin: 0 0 .8em; padding: 0; text-align: center; }
    }
    @media only screen and (max-width: 750px) {
      #shopify-pc__prefs__dialog .pc-title--prefs { text-align: start; padding: 0 25px 0 0; }
    }

    /* Compact bar instead of Shopify's 720x216 card: one strip along the
       bottom edge, text beside the buttons on desktop and above them on a
       phone. The title stays in the DOM (aria-labelledby points at it) but
       is visually hidden. Accept and decline keep equal weight. */
    #shopify-pc__banner.shopify-pc__banner__dialog {
      left: 0 !important; right: 0 !important; bottom: 0 !important;
      width: 100% !important; max-width: none !important;
      margin: 0 !important; padding: 12px 24px !important;
      border-radius: 0 !important;
      border-top: 1px solid hsl(0 0% 88%);
      box-shadow: 0 -4px 16px hsl(0 0% 0% / .06) !important;
    }
    #shopify-pc__banner .shopify-pc__banner__wrapper {
      display: flex !important; flex-direction: row !important; flex-wrap: nowrap !important;
      align-items: center !important; gap: 24px;
      max-width: 1280px; margin: 0 auto !important; padding: 0 !important;
    }
    #shopify-pc__banner .shopify-pc__banner__body { flex: 1; min-width: 0; margin: 0 !important; }
    #shopify-pc__banner .pc-title--banner {
      position: absolute; width: 1px; height: 1px; overflow: hidden;
      clip: rect(0 0 0 0); white-space: nowrap; margin: 0;
    }
    #shopify-pc__banner .shopify-pc__banner__body p:not(.pc-title) {
      font-size: 13px; line-height: 1.45; margin: 0;
    }
    #shopify-pc__banner .shopify-pc__banner__btns {
      display: flex !important; flex-direction: row !important; align-items: center;
      flex-wrap: nowrap !important;
      gap: 8px; margin: 0 !important; flex-shrink: 0; width: auto !important;
    }
    #shopify-pc__banner .shopify-pc__banner__btns button {
      width: auto !important; min-width: 0 !important; margin: 0 !important;
      padding: 8px 18px !important; font-size: 13px !important; line-height: 1.2 !important;
      min-height: 36px;
    }
    #shopify-pc__banner #shopify-pc__banner__btn-manage-prefs { padding: 8px 6px !important; }
    @media only screen and (max-width: 750px) {
      #shopify-pc__banner.shopify-pc__banner__dialog { padding: 10px 16px !important; }
      #shopify-pc__banner .shopify-pc__banner__wrapper { flex-direction: column !important; align-items: stretch !important; gap: 8px; }
      #shopify-pc__banner #shopify-pc__banner__btn-manage-prefs { flex: none; font-size: 12px !important; }
      #shopify-pc__banner .shopify-pc__banner__body p:not(.pc-title) { font-size: 12px; }
      #shopify-pc__banner .shopify-pc__banner__btns button:not(#shopify-pc__banner__btn-manage-prefs) { flex: 1; }
    }`;
  document.head.append(style);

  // A heading becomes a <p> with the same id and classes, so aria-labelledby
  // keeps pointing at it; `pc-title` restores the look.
  const unheading = (el, variant) => {
    if (!el || !/^H[1-6]$/.test(el.tagName)) return el;
    const p = document.createElement('p');
    for (const { name, value } of el.attributes) p.setAttribute(name, value);
    p.classList.add('pc-title', `pc-title--${variant}`);
    p.innerHTML = el.innerHTML;
    el.replaceWith(p);
    return p;
  };

  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };

  const translateBanner = (banner) => {
    unheading(banner.querySelector('#shopify-pc__banner__body-title'), 'banner');
    for (const [id, text] of Object.entries(TEXT)) setText(banner.querySelector(`#${id}`), text);
    // "Manage preferences" wraps its label in a <span>.
    setText(banner.querySelector('#shopify-pc__banner__btn-manage-prefs span'), TEXT['shopify-pc__banner__btn-manage-prefs']);

    const link = banner.querySelector('#shopify-pc__banner__body-policy-link');
    const body = link?.closest('p');
    if (body && !body.dataset.sr) {
      body.dataset.sr = '1';
      link.textContent = 'Politici privatnosti';
      link.href = POLICY_URL;
      body.replaceChildren(
        'Mi i naši partneri, uključujući Shopify, koristimo kolačiće za prilagođavanje sajta, oglase i merenje posećenosti – tek kada ih prihvatite. Više u ',
        link,
        '.'
      );
    }
  };

  const translatePrefs = (dialog) => {
    unheading(dialog.querySelector('#shopify-pc__prefs__header-title'), 'prefs');
    unheading(dialog.querySelector('.shopify-pc__prefs__intro-main h3'), 'intro');
    for (const [id, text] of Object.entries(TEXT)) setText(dialog.querySelector(`#${id}`), text);
    dialog.querySelector('#shopify-pc__prefs__header-close')?.setAttribute('aria-label', 'Zatvori');

    const intro = dialog.querySelector('.shopify-pc__prefs__intro-main');
    setText(intro?.querySelector('.pc-title'), 'Vi odlučujete o svojim podacima');
    setText(intro?.querySelector('p:not(.pc-title)'), 'Saznajte koje kolačiće koristimo i izaberite koje dozvoljavate.');

    for (const [id, [label, desc]] of Object.entries(OPTIONS)) {
      const row = dialog.querySelector(`#${id}`);
      if (!row) continue;
      // The label's first child is its text; the checkbox and its icon follow.
      const text = row.querySelector('label')?.firstChild;
      if (text?.nodeType === Node.TEXT_NODE && text.textContent !== label) text.textContent = label;
      setText(row.querySelector(':scope > p'), desc);
    }
  };

  const run = () => {
    const banner = document.getElementById('shopify-pc__banner');
    if (banner) translateBanner(banner);
    const prefs = document.getElementById('shopify-pc__prefs__dialog');
    if (prefs) translatePrefs(prefs);
  };

  // Shopify injects the banner late and builds the preferences dialog on demand.
  // One pass per frame, however many mutations the page makes in it.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      run();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  run();
})();
